import React, { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { currencyService } from '../../services/currencyService'
import { EditIcon } from '../icons/Icons'
import s from './shared.module.css'

const CURRENCIES = ['USD', 'GBP', 'INR'] as const
const SYMBOLS: Record<string, string> = { USD: '$', GBP: '£', INR: '₹' }
const DEFAULTS: Record<string, Record<string, number>> = {
  USD: { USD: 1.0, GBP: 0.79, INR: 83.12 },
  GBP: { USD: 1.27, GBP: 1.0, INR: 105.56 },
  INR: { USD: 0.012, GBP: 0.0095, INR: 1.0 },
}

export default function CurrencySettings() {
  const { organization } = useAuth()
  const orgId = organization!.id
  const [rates, setRates] = useState<Map<string, Map<string, number>>>(new Map())
  const [editing, setEditing] = useState<{ from: string; to: string } | null>(null)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const loadRates = async () => {
    setLoading(true)
    await currencyService.loadExchangeRates(orgId)
    setRates(currencyService.getExchangeRatesMap())
    setLoading(false)
  }

  useEffect(() => { loadRates() }, [orgId])

  const handleEdit = (from: string, to: string) => {
    if (from === to) return
    setEditing({ from, to })
    setEditValue(rates.get(from)?.get(to)?.toString() || '')
  }

  const handleSave = async () => {
    if (!editing || saving) return
    const newRate = parseFloat(editValue)
    if (isNaN(newRate) || newRate <= 0) { alert('Enter a valid positive number'); return }
    setSaving(true)
    setError(null)
    try {
      await currencyService.updateExchangeRate(orgId, editing.from, editing.to, newRate)
      setRates(currencyService.getExchangeRatesMap())
      setEditing(null)
      setEditValue('')
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update exchange rate')
      setTimeout(() => setError(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    if (!confirm('Reset all exchange rates to default values?')) return
    setSaving(true)
    setError(null)
    try {
      for (const from of CURRENCIES) {
        for (const to of CURRENCIES) {
          if (from !== to) {
            await currencyService.updateExchangeRate(orgId, from, to, DEFAULTS[from][to])
          }
        }
      }
      await loadRates()
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset exchange rates')
      setTimeout(() => setError(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#605e5c' }}>Loading currency settings...</div>

  return (
    <div>
      <div className={s.pageHeader}>
        <div>
          <h2 className={s.pageTitle}>Currency Settings</h2>
          <p className={s.pageSubtitle}>Manage exchange rates for multi-currency calculations</p>
        </div>
        <button className={s.secondaryBtn} onClick={handleReset} disabled={saving}>Reset to Defaults</button>
      </div>

      {success && (
        <div style={{ padding: '10px 14px', background: '#e6f4ea', border: '1px solid #a8d5a2', borderRadius: 4, marginBottom: 16, color: '#107c10', fontSize: 13 }}>
          Exchange rates updated successfully.
        </div>
      )}

      {error && (
        <div style={{ padding: '10px 14px', background: '#fde7e9', border: '1px solid #f1aeb5', borderRadius: 4, marginBottom: 16, color: '#c50f1f', fontSize: 13 }}>
          {error}
        </div>
      )}

      <div className={s.card} style={{ marginBottom: 20 }}>
        <div className={s.cardHeader}>
          <span className={s.cardTitle}>Exchange Rates Matrix</span>
          <span style={{ fontSize: 12, color: '#605e5c' }}>Click the edit icon to update a rate</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>From / To</th>
                {CURRENCIES.map(c => <th key={c}>{SYMBOLS[c]} {c}</th>)}
              </tr>
            </thead>
            <tbody>
              {CURRENCIES.map(from => (
                <tr key={from}>
                  <td style={{ fontWeight: 600 }}>{SYMBOLS[from]} {from}</td>
                  {CURRENCIES.map(to => {
                    const rate = rates.get(from)?.get(to) ?? (from === to ? 1 : undefined)
                    const isEditing = editing?.from === from && editing?.to === to
                    if (from === to) return <td key={to} style={{ color: '#605e5c' }}>1.0000</td>
                    return (
                      <td key={to}>
                        {isEditing ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <input
                              type="text"
                              value={editValue}
                              onChange={e => {
                                const v = e.target.value.replace('-', '')
                                if (/^\d*\.?\d*$/.test(v)) setEditValue(v)
                              }}
                              style={{ width: 80, padding: '3px 6px', fontSize: 12, border: '1px solid #0078d4', borderRadius: 2 }}
                              autoFocus
                              onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') { setEditing(null); setEditValue('') } }}
                            />
                            <button className={s.primaryBtn} style={{ padding: '3px 8px', fontSize: 11 }} onClick={handleSave} disabled={saving}>
                              {saving ? '...' : 'Save'}
                            </button>
                            <button className={s.secondaryBtn} style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => { setEditing(null); setEditValue('') }}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <span>{rate !== undefined ? Number(rate).toFixed(4) : '—'}</span>
                            <button
                              className={`${s.actionBtn} ${s.edit}`}
                              onClick={() => handleEdit(from, to)}
                              style={{ padding: 4 }}
                            >
                              <EditIcon size={12} />
                            </button>
                          </div>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={s.card}>
        <div className={s.cardHeader}><span className={s.cardTitle}>Conversion Examples</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, padding: 16 }}>
          {[
            { from: 'USD', to: 'GBP', amount: 100 },
            { from: 'USD', to: 'INR', amount: 100 },
            { from: 'GBP', to: 'INR', amount: 100 },
            { from: 'INR', to: 'USD', amount: 1000 },
          ].map(ex => (
            <div key={`${ex.from}-${ex.to}`} style={{ padding: '12px 16px', background: '#faf9f8', border: '1px solid #edebe9', borderRadius: 4 }}>
              <div style={{ fontSize: 12, color: '#605e5c', marginBottom: 4 }}>{SYMBOLS[ex.from]}{ex.amount} {ex.from} =</div>
              <div style={{ fontWeight: 600, color: '#323130' }}>
                {currencyService.convertAndFormat(ex.amount, ex.from, ex.to, 2)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 16, padding: '10px 14px', background: '#fff8e0', border: '1px solid #f3d77e', borderRadius: 4, fontSize: 13, color: '#605e5c' }}>
        <strong>Note:</strong> Exchange rates are stored per organization and used for all financial calculations and reports. Changes take effect immediately.
      </div>
    </div>
  )
}
